import React from "react";
import { CoRetrieveDocumentData } from "../../components/generic/CoRetrieveDocumentData";
import { DocumentNotValidError } from "../../components/generic/DocumentNotValidError";
import { AllCoObjectsSelector } from "../../components/generic/AllCoObjectsSelector";
import { ObjectsListUpdatePanel } from "../../components/coobjects/object-list-panel/ObjectsListUpdatePanel";
import { ObjectsListUpdateFooter } from "../../components/coobjects/object-list-panel/ObjectsListUpdateFooter";
import { CoObjectItem } from "../../components/interfaces/CoObject";
import { DocumentPart } from "../../api/models";
import { DocumentPartMetadata } from "../../api/models/document-part-metadata";
import { ObjectsListDeleteFooter } from "../../components/coobjects/object-list-panel/ObjectsListDeleteFooter";
import { ObjectsListLockFooter } from "../../components/coobjects/object-list-panel/ObjectsListLockFooter";
import { UserFunctionEnum } from "../../api/models/user-function-enum";
import { UnauthorizedError } from "../../components/generic/UnauthorizedError";
import { ProtectedComponent } from "../../components/wrappers/ProtectedComponent";

interface ObjectsListFooterProps {
  showUpdateButton?: boolean;
  showDeleteButton?: boolean;
  showLockButton?: boolean | "lock" | "unlock" | "disabled-lock";
  selectedObjects: Map<string, CoObjectItem>;
  username: string;
  documentPart: DocumentPart;
  documentPartMetadata: DocumentPartMetadata;
  onDelete?: () => void;
  onToggleLock?: () => void;
}

const ObjectsListFooterComponent: React.FC<ObjectsListFooterProps> = (props) => {
  const {
    showUpdateButton,
    showDeleteButton,
    showLockButton,
    selectedObjects,
    username,
    documentPart,
    documentPartMetadata,
    onDelete,
    onToggleLock,
  } = props;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "start",
        gap: "6px",
        flexDirection: "row",
        flexWrap: "wrap",
      }}
    >
      {showUpdateButton && (
        <ObjectsListUpdateFooter
          selectedObjects={selectedObjects}
          username={username}
          documentPart={documentPart}
          documentPartMetadata={documentPartMetadata}
        />
      )}
      {showDeleteButton && (
        <ObjectsListDeleteFooter
          disabled={selectedObjects.size === 0 || Array.from(selectedObjects.values()).some((o) => o.isLocked)}
          count={selectedObjects.size}
          onDelete={onDelete ?? (() => {})}
        />
      )}
      {showLockButton !== false && (
        <ObjectsListLockFooter
          disabled={showLockButton === "disabled-lock" || selectedObjects.size === 0}
          type={showLockButton === "unlock" ? "unlock" : "lock"}
          onToggleLock={onToggleLock ?? (() => {})}
        />
      )}
    </div>
  );
};

export const ObjectsListPage: React.FunctionComponent = () => {
  return (
    <ProtectedComponent userFunction={UserFunctionEnum.MENU_OBJECT_LIST} errorComponent={<UnauthorizedError />}>
      <CoRetrieveDocumentData shouldFetchDocumentPart={true} shouldFetchDocumentPartMetadata={true}>
        {({ coObject, documentPart, documentPartMetadata: documentMetadata }) =>
          coObject?.documentId ? (
            <AllCoObjectsSelector>
              {(coObjectsArray, reloadCoObjects) => (
                <ObjectsListUpdatePanel
                  documentPart={documentPart}
                  documentPartMetadata={documentMetadata}
                  documentCoObjects={coObjectsArray.filter(
                    (coObject) => coObject.info.objectType !== "DOCUMENT_PART_INCLUDE"
                  )}
                  reloadCoObjects={reloadCoObjects}
                  footerComponents={ObjectsListFooterComponent}
                  page="objectsList"
                />
              )}
            </AllCoObjectsSelector>
          ) : (
            <DocumentNotValidError />
          )
        }
      </CoRetrieveDocumentData>
    </ProtectedComponent>
  );
};
